import { useInstitutionsStore } from '@/stores/institutions';
import { SESSION_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from '@/utils/constants';
import { getInstitutionWhitelabelSubdomain } from '@/utils/institutionsUtils';
import { navigateToLogin } from '@/utils/navigateToLogin';
import { useCallback } from 'react';

/**
 * Returns a function that triggers a full page reload to switch the current institution.
 * All institution switches (same or cross-subdomain) perform a redirect to the login page
 * with the target institution's ID and current authentication token.
 * @returns Institution change handler accepting an ID and optional returnPath.
 */
export const useInstitutionChange = () => {
  const { currentInstitution, getInstitutionById } = useInstitutionsStore(
    (state) => ({
      currentInstitution: state.currentInstitution,
      getInstitutionById: state.getInstitutionById,
    }),
  );
  const handleInstitutionChange = useCallback(
    (id: string, redirect?: string) => {
      const institution = getInstitutionById(id);
      if (
        !institution ||
        !currentInstitution?.id ||
        institution.id === currentInstitution.id
      )
        return;

      if (redirect) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_PATH, redirect);
      }

      const currentSubdomain = getInstitutionWhitelabelSubdomain(
        currentInstitution.whitelabel,
      );
      const targetSubdomain = getInstitutionWhitelabelSubdomain(
        institution.whitelabel,
      );

      // If subdomains differ, keep the current institution ID in this subdomain's localStorage.
      // This prevents unwanted redirects when this subdomain is visited again.
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
        targetSubdomain === currentSubdomain ? id : currentInstitution?.id,
      );

      navigateToLogin({
        subdomain: targetSubdomain,
        attachToken: true,
        institutionId: id,
      });
    },
    [
      currentInstitution?.id,
      currentInstitution?.whitelabel,
      getInstitutionById,
    ],
  );

  return handleInstitutionChange;
};
